import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table, Button, Input, Form } from "reactstrap";
import { Link } from "react-router-dom";
import { TextInput } from "../FormInputs";
import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import Dragula from "react-dragula";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rankItem } from "@tanstack/match-sorter-utils";
import { useSelector, useDispatch } from "react-redux";
import { setinitial_page, setsorting_data } from "../../../slices/globalslice";
import useNavigationMenu from "../../../hooks/useNavigationMenu";
import Loader2 from "../Loader2";

const TableContainer = ({
  columns,
  perPageNotRequired,
  isLoading,
  setperPage,
  data,
  total,
  pagechange,
  disableSearch,
  customPageSize,
  tableClass,
  draggable,
  trClass,
  thClass,
  divClass,
  disableNavigation,
  currentPage,
  onPageChange,
  onRowClick,
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const { updatemenudisplay } = useNavigationMenu();
  const dispatch = useDispatch();
  const [sorting, setSorting] = useState([]);

  const initial_page = useSelector((state) => state.globalslice.initial_page);
  // const perPage = useSelector(state => state.globalslice.perPage);
  const sorting_data = useSelector((state) => state.globalslice.sorting_data);
  const queryClient = useQueryClient();

  // Check if URL-based pagination is enabled
  const isURLPaginationEnabled =
    currentPage !== undefined && onPageChange !== undefined;

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const isMobile = window.innerWidth <= 576;

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      sorting,
    },
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: false,
    initialState: {
      pagination: {
        pageIndex: isURLPaginationEnabled ? currentPage - 1 : 0,
        pageSize: customPageSize || 10,
      },
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      setSorting(newSorting);
      table.setPageIndex(0);

      if (isURLPaginationEnabled && onPageChange) {
        onPageChange(1);
      }
    },
    pageCount: Math.ceil(total / customPageSize),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = table;

  useEffect(() => {
    customPageSize && setPageSize(customPageSize);
  }, [customPageSize, setPageSize]);

  // Handle URL pagination changes - but prevent infinite loops
  useEffect(() => {
    if (isURLPaginationEnabled && currentPage) {
      const currentTablePage = getState().pagination.pageIndex + 1;
      if (currentTablePage !== currentPage) {
        setPageIndex(currentPage - 1);
      }
    }
  }, [currentPage, isURLPaginationEnabled]);

  useEffect(() => {
    const pageIndex = table.getState().pagination.pageIndex;
    dispatch(setinitial_page(pageIndex + 1));
  }, [table.getState().pagination.pageIndex]);

  useEffect(() => {
    if (pagechange !== undefined) {
      table.setPageIndex(0);
      dispatch(setinitial_page(1));

      if (isURLPaginationEnabled && onPageChange) {
        onPageChange(1);
      }
    }
  }, [pagechange]);

  useEffect(() => {
    dispatch(setsorting_data({}));
  }, []);

  useEffect(() => {
    const pageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;
    const sortBy = sorting.length ? sorting[0].id : "";
    const sortOrder = sorting.length ? (sorting[0].desc ? "desc" : "asc") : "";

    if (sortBy != "" && sortOrder != "") {
      dispatch(
        setsorting_data({
          sortBy: sortBy,
          sortOrder: sortOrder,
        })
      );
    }

    dispatch(setinitial_page(pageIndex + 1));
  }, [table.getState().pagination.pageIndex, sorting]);

  const ASC_ICON = "▲";
  const DESC_ICON = "▼";
  const [search_value, setsearch_value] = useState("");

  const handleChange = (e) => {
    setsearch_value(e.target.value);
  };

  const handleSubmit = () => {
    dispatch(
      setsorting_data({
        ...sorting_data,
        search: search_value,
      })
    );
    dispatch(setinitial_page(1));

    setPageIndex(0);
    if (isURLPaginationEnabled && onPageChange) {
      onPageChange(1);
    }
  };

  const handleClose = () => {
    setsearch_value('')
    dispatch(
      setsorting_data({
        ...sorting_data,
        search: '',
      })
    );
    dispatch(setinitial_page(1));
    setPageIndex(0);
    if (isURLPaginationEnabled && onPageChange) {
      onPageChange(1);
    }
  };

  // Fixed page change handlers
  const handlePreviousPage = (e) => {
    e.preventDefault();
    const currentPageIndex = getState().pagination.pageIndex;
    if (currentPageIndex > 0) {
      const newPageIndex = currentPageIndex - 1;
      setPageIndex(newPageIndex);
      if (isURLPaginationEnabled && onPageChange) {
        onPageChange(newPageIndex + 1);
      }
    }
  };

  const handleNextPage = (e) => {
    e.preventDefault();
    const currentPageIndex = getState().pagination.pageIndex;
    const totalPages = getPageOptions().length;
    if (currentPageIndex < totalPages - 1) {
      const newPageIndex = currentPageIndex + 1;
      setPageIndex(newPageIndex);
      if (isURLPaginationEnabled && onPageChange) {
        onPageChange(newPageIndex + 1);
      }
    }
  };

  const handlePageIndexChange = (e, pageIndex) => {
    e.preventDefault();
    setPageIndex(pageIndex);
    if (isURLPaginationEnabled && onPageChange) {
      onPageChange(pageIndex + 1);
    }
  };

  const dragulaDecorator = (componentBackingInstance) => {
    if (draggable && componentBackingInstance) {
      const drake = Dragula([componentBackingInstance]);

      drake.on("drop", (el, target) => {
        const rows = target.querySelectorAll("tr");
        const newOrder = Array.from(rows).map((row, index) => ({
          id: row.getAttribute("data-id"),
          position: index + 1,
        }));

        const input_data = {
          type: "menu",
          data: newOrder,
        };

        updatemenudisplay.mutate(input_data, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["menu_list"] });
          },
        });

        drake.cancel(true);
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setperPage(newSize)
    table.setPageSize(newSize);
    table.setPageIndex(0);

    if (isURLPaginationEnabled && onPageChange) {
      onPageChange(1);
    }
  };

  return (
    <Fragment>
      <>
        {isLoading ? (
          <div
            style={{ height: "400px" }}
            className="d-flex justify-content-center align-items-center"
          >
            <Loader2 />
          </div>
        ) : (
          <>
            <div className={`${isMobile ? 'd-block' : 'd-flex'} justify-content-between align-items-center mb-3`}>
              {!perPageNotRequired ? (
                <div className={`d-flex align-items-center ${isMobile ? 'mb-3' : 'mb-0'}`}>
                  <label className="me-2 mb-0">Rows per page:</label>
                  <Input
                    type="select"
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={customPageSize}
                    onChange={handlePageSizeChange}
                  >
                    {[10, 20, 50, 100, 200, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </Input>
                </div>
              ) : <div className={`d-flex align-items-center ${isMobile ? 'mb-3' : 'mb-0'}`}></div>}

              {disableSearch === undefined && (
                <div className="d-flex align-items-center">

                  <div style={{ position: "relative" }}>
                    <Input
                      type="text"
                      className="form-control-sm search_custom_width"
                      onChange={handleChange}
                      value={search_value}
                      onKeyDown={handleKeyDown}
                      style={{ paddingRight: "30px" }}
                    />

                    {search_value && (
                      <i
                        className="ri-close-line"
                        onClick={handleClose}
                        style={{
                          position: "absolute",
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          fontSize: "16px",
                          color: "#999"
                        }}
                      />
                    )}
                  </div>

                  <Button
                    color="primary"
                    className="btn-icon btn-sm ms-2"
                    onClick={handleSubmit}
                  >
                    <i className="ri-search-line" />
                  </Button>

                </div>

              )}
            </div>

            <>
              {data?.length <= 0 ? (
                <div className="d-flex justify-content-center">
                  No Data found
                </div>
              ) : (
                <>
                  <div
                    className={`${divClass} max-w-full pb-8 ${isMobile && "overflow-x-scroll"
                      } overflow-y-hidden`}
                             style={{
                        paddingBottom:"80px"
                        }}
                  >
                    <Table hover className={`${tableClass} w-full table-sm`}>
                      <thead className={"table-light"}>
                        {getHeaderGroups().map((headerGroup) => (
                          <tr className={trClass} key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <th
                                key={header.id}
                                className={`${thClass} cursor-pointer`}
                                {...{
                                  onClick:
                                    header.column.getToggleSortingHandler(),
                                }}
                              >
                                {header.isPlaceholder ? null : (
                                  <>
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                    <span className="ms-1">
                                      {header.column.getIsSorted() === "asc" &&
                                        ASC_ICON}
                                      {header.column.getIsSorted() === "desc" &&
                                        DESC_ICON}
                                    </span>
                                  </>
                                )}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody ref={dragulaDecorator}>
                        {getRowModel().rows.map((row) => (
                          <tr
                            key={row.id}
                            data-id={row.original.id || row.original.menuid}
                            onClick={() => onRowClick?.(row)}
                            style={{ cursor: onRowClick ? "pointer" : "default" }}
                          >
                            {row.getVisibleCells().map((cell) => {
                              return (
                                <td key={cell.id}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {disableNavigation == undefined && (
                    <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                      <div className="col-sm">
                        <div className="text-muted">
                          <div className="text-muted">
                            Showing{" "}
                            <span className="fw-semibold">
                              {Math.min(initial_page * customPageSize, total)}
                            </span>{" "}
                            of <span className="fw-semibold">{total}</span> Results
                          </div>

                        </div>
                      </div>
                      <div className="col-sm-auto">
                        <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                          <li
                            className={
                              !getCanPreviousPage()
                                ? "page-item disabled"
                                : "page-item"
                            }
                          >
                            <Link
                              to="#"
                              className="page-link"
                              onClick={handlePreviousPage}
                            >
                              {isMobile ? "<" : "Previous"}
                            </Link>
                          </li>

                          {(() => {
                            const currentPageIndex =
                              getState().pagination.pageIndex;
                            const totalPages = getPageOptions().length;
                            const visiblePages = isMobile ? 2 : 5;

                            const pages = [];
                            const half = Math.floor(visiblePages / 2);
                            let start = Math.max(0, currentPageIndex - half);
                            let end = start + visiblePages;
                            if (end > totalPages) {
                              end = totalPages;
                              start = Math.max(0, end - visiblePages);
                            }
                            if (start > 0) {
                              pages.push(
                                <li key="first" className="page-item">
                                  <Link
                                    to="#"
                                    className="page-link"
                                    onClick={(e) => handlePageIndexChange(e, 0)}
                                  >
                                    1
                                  </Link>
                                </li>
                              );
                              if (start > 1) {
                                pages.push(
                                  <li
                                    key="start-dots"
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                            }
                            for (let i = start; i < end; i++) {
                              pages.push(
                                <li key={i} className="page-item">
                                  <Link
                                    to="#"
                                    className={
                                      currentPageIndex === i
                                        ? "page-link active"
                                        : "page-link"
                                    }
                                    onClick={(e) => handlePageIndexChange(e, i)}
                                  >
                                    {i + 1}
                                  </Link>
                                </li>
                              );
                            }

                            if (end < totalPages) {
                              if (end < totalPages - 1) {
                                pages.push(
                                  <li
                                    key="end-dots"
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              pages.push(
                                <li key="last" className="page-item">
                                  <Link
                                    to="#"
                                    className="page-link"
                                    onClick={(e) =>
                                      handlePageIndexChange(e, totalPages - 1)
                                    }
                                  >
                                    {totalPages}
                                  </Link>
                                </li>
                              );
                            }

                            return pages;
                          })()}

                          <li
                            className={
                              !getCanNextPage()
                                ? "page-item disabled"
                                : "page-item"
                            }
                          >
                            <Link
                              to="#"
                              className="page-link"
                              onClick={handleNextPage}
                            >
                              {isMobile ? ">" : "Next"}
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </Row>
                  )}

                </>
              )}
            </>
          </>
        )}
      </>
    </Fragment>
  );
};

export default TableContainer;
